import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { BrandsIcon } from '../assets';
import { Tab } from '../components/campaigns/SingleCampaignTabs';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { LateralProfileBrandMenu } from '../components/common/LateralProfileBrandMenu';
import { ProposalSubrowTable } from '../components/proposals/ProposalSubrowsTable';
import DataTable from '../components/tables/DataTable';
import { getProposalsTableHeaders } from '../components/tables/headers/proposals';
import { replaceIds, ROUTES } from '../config/routes/config';
import { GET_BRAND } from '../graphql/brands/query';
import { GET_PROPOSALS } from '../graphql/proposal/query';
import { useNotification } from '../hooks/useNotification';
import { usePermissions } from '../hooks/usePermissions';
import { useTableDataAndFilters } from '../hooks/useTableDataAndFilters';
import { proposalFilterAtom, userAtom } from '../states/atoms';
import type {
  ProposalFiltersAtom,
  ProposalTable,
  ResProposalsList,
} from '../types/proposal';

export const SingleBrandProposals = () => {
  const { id } = useParams();
  const { t } = useTranslation('common');
  const myUser = useRecoilValue(userAtom);

  const navigate = useNavigate();
  const { notifyWarning } = useNotification();
  const location = useLocation();

  const { isInfluentia, isInfluencer } = usePermissions();
  const [brand, setBrand] = useState<any>({});
  const { data } = useQuery(GET_BRAND, {
    variables: {
      brandId: id,
    },
    onError: () => {
      notifyWarning(t('global.errorSingleBrand'));
      navigate(ROUTES.BRANDS);
    },
    onCompleted: (info: any) => {
      setBrand(info.brand.data);
    },
    skip: !id,
  });

  const {
    data: proposals,
    resetPagination,
    total,
    filters,
    setFilters,
    table,
    error,
    loading,
    refetch: refetchTable,
  } = useTableDataAndFilters<
    ProposalFiltersAtom,
    ProposalTable,
    {},
    ResProposalsList
  >({
    filterAtom: proposalFilterAtom,
    getColumns: getProposalsTableHeaders,
    columnsExtras: {},
    responseAccessKey: 'proposals',
    query: GET_PROPOSALS,
    subrowAccesor: 'ProposalUsers',
  });

  useEffect(() => {
    if (brand) {
      setFilters((prev) => ({
        ...prev,
        client: [{ label: '', value: brand?.id || '' }],
      }));
      refetchTable();
    }
    return () => {
      resetPagination();
      setFilters((prev) => ({
        ...prev,
        client: undefined,
      }));
    };
  }, [brand]);

  const handleTableClick = (idClick: string) => {
    const proposal = proposals.find((c) => c.id === idClick);
    if (proposal)
      navigate(
        replaceIds({
          url: ROUTES.SINGLE_PROPOSAL_GENERAL,
          params: [
            {
              proposalId: proposal.id,
            },
          ],
        }),
      );
  };

  return (
    <div className="w-full flex flex-col gap-y-5">
      <Breadcrumbs
        icon={<BrandsIcon />}
        breadcrumbs={[
          {
            text: t('singleBrand.Breadcrumps'),
            link: ROUTES.BRANDS,
          },
          {
            text: data?.brand.data.name || '',
            link: ROUTES.SINGLE_BRAND.replace(':id', brand.id),
          },
          {
            text: t('proposals.title'),
          },
        ]}
      />
      <div>
        {isInfluentia() && (
          <div className="flex-row gap-x-2 overflow-x-auto customScroll pb-1 lg:hidden flex">
            <Tab
              to={ROUTES.SINGLE_BRAND.replace(':id', brand.id)}
              text={t('profile.menu.title1')}
            />
            <Tab
              to={ROUTES.SINGLE_BRAND_MEMBERS.replace(':id', brand.id)}
              text={t('profile.menu.members')}
            />
            <Tab
              to={ROUTES.SINGLE_BRAND_CAMPAIGNS.replace(':id', brand.id)}
              text={t('profile.menu.title2')}
            />
            <Tab
              to={ROUTES.SINGLE_BRAND_PROPOSALS.replace(':id', brand.id)}
              text={t('profile.menu.title5')}
            />
          </div>
        )}
        <div className="flex flex-row gap-12">
          {isInfluentia() && <LateralProfileBrandMenu brand={brand} />}
          <div>
            <DataTable
              error={error}
              handleRowClick={(idClick) => handleTableClick(idClick)}
              handleSubrowClick={(idClick) => handleTableClick(idClick)}
              loading={loading}
              table={table}
              total={total}
              tableId="ProposalsTable"
              sorting={{ ...filters }}
              SubrowsComponent={ProposalSubrowTable}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
